import { MoreHorizontal, Settings2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Header,
  Menu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Separator,
  type Selection,
} from 'react-aria-components';
import {
  usePreferences,
  type AnimationSpeedPreference,
  type AnimationTogglePreference,
  type DensityPreference,
  type MotionPreference,
  type ThemePreference,
} from '../state/preferences.ts';
import styles from './PreferencesMenu.module.css';

const themes: { id: ThemePreference; label: string }[] = [
  { id: 'system', label: 'Match system' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
];

const motions: { id: MotionPreference; label: string }[] = [
  { id: 'system', label: 'Match system' },
  { id: 'reduced', label: 'Reduce motion' },
];

const densities: { id: DensityPreference; label: string }[] = [
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'compact', label: 'Compact' },
];

const animations: { id: AnimationTogglePreference; label: string }[] = [
  { id: 'on', label: 'Animations on' },
  { id: 'off', label: 'Animations off' },
];

const animationSpeeds: { id: AnimationSpeedPreference; label: string }[] = [
  { id: 'slow', label: 'Slow (0.5×)' },
  { id: 'normal', label: 'Normal (1.0×)' },
  { id: 'fast', label: 'Fast (2.0×)' },
];

// Item keys must be unique across the whole menu, so each section prefixes its own.
function single<T extends string>(selection: Selection, prefix: string): T | null {
  if (selection === 'all') return null;
  const [first] = selection;
  return typeof first === 'string' && first.startsWith(prefix)
    ? (first.slice(prefix.length) as T)
    : null;
}

export function PreferencesMenu({
  presentation = 'toolbar',
}: {
  presentation?: 'toolbar' | 'mobile';
}) {
  const {
    theme,
    motion,
    density,
    animation,
    animationSpeed,
    setTheme,
    setMotion,
    setDensity,
    setAnimation,
    setAnimationSpeed,
  } = usePreferences();
  const navigate = useNavigate();
  const mobile = presentation === 'mobile';

  return (
    <MenuTrigger>
      <Button
        className={`${styles.trigger} ${mobile ? styles.mobileTrigger : styles.toolbarTrigger}`}
        aria-label={mobile ? 'More workspace options' : 'Display preferences'}
      >
        {mobile ? (
          <MoreHorizontal size={20} aria-hidden="true" />
        ) : (
          <Settings2 size={20} aria-hidden="true" />
        )}
        {mobile && <span>More</span>}
      </Button>
      <Popover className={styles.popover} placement={mobile ? 'top end' : 'bottom end'} offset={8}>
        <Menu
          className={styles.menu}
          aria-label={mobile ? 'More workspace options' : 'Display preferences'}
        >
          {mobile ? (
            // The phone tab bar's More: the pages that don't fit the bar. Members, the
            // organization, your profile and display preferences are all in Settings.
            <>
              <MenuItem
                id="remediations"
                className={`${styles.item} ${styles.link}`}
                onAction={() => void navigate({ to: '/remediations' })}
              >
                Remediations
              </MenuItem>
              <MenuItem
                id="authorization"
                className={`${styles.item} ${styles.link}`}
                onAction={() => void navigate({ to: '/authorization' })}
              >
                Authorization
              </MenuItem>
              <MenuItem
                id="audit-logs"
                className={`${styles.item} ${styles.link}`}
                onAction={() => void navigate({ to: '/audit-logs' })}
              >
                Audit logs
              </MenuItem>
              <MenuItem
                id="settings"
                className={`${styles.item} ${styles.link}`}
                onAction={() => void navigate({ to: '/settings' })}
              >
                Settings
              </MenuItem>
              <MenuItem
                id="help"
                className={`${styles.item} ${styles.link}`}
                onAction={() => void navigate({ to: '/help' })}
              >
                Help
              </MenuItem>
            </>
          ) : (
            <>
              <MenuSection
                selectionMode="single"
                selectedKeys={[`theme:${theme}`]}
                disallowEmptySelection
                onSelectionChange={(s) => {
                  const next = single<ThemePreference>(s, 'theme:');
                  if (next) setTheme(next);
                }}
              >
                <Header className={styles.header}>Theme</Header>
                {themes.map((t) => (
                  <MenuItem key={t.id} id={`theme:${t.id}`} className={styles.item}>
                    {t.label}
                  </MenuItem>
                ))}
              </MenuSection>
              <Separator className={styles.separator} />
              <MenuSection
                selectionMode="single"
                selectedKeys={[`motion:${motion}`]}
                disallowEmptySelection
                onSelectionChange={(s) => {
                  const next = single<MotionPreference>(s, 'motion:');
                  if (next) setMotion(next);
                }}
              >
                <Header className={styles.header}>Motion</Header>
                {motions.map((m) => (
                  <MenuItem key={m.id} id={`motion:${m.id}`} className={styles.item}>
                    {m.label}
                  </MenuItem>
                ))}
              </MenuSection>
              <Separator className={styles.separator} />
              <MenuSection
                selectionMode="single"
                selectedKeys={[`animation:${animation}`]}
                disallowEmptySelection
                onSelectionChange={(s) => {
                  const next = single<AnimationTogglePreference>(s, 'animation:');
                  if (next) setAnimation(next);
                }}
              >
                <Header className={styles.header}>Animations</Header>
                {animations.map((a) => (
                  <MenuItem key={a.id} id={`animation:${a.id}`} className={styles.item}>
                    {a.label}
                  </MenuItem>
                ))}
              </MenuSection>
              <Separator className={styles.separator} />
              <MenuSection
                selectionMode="single"
                selectedKeys={[`speed:${animationSpeed}`]}
                disallowEmptySelection
                onSelectionChange={(s) => {
                  const next = single<AnimationSpeedPreference>(s, 'speed:');
                  if (next) setAnimationSpeed(next);
                }}
              >
                <Header className={styles.header}>Speed</Header>
                {animationSpeeds.map((sp) => (
                  <MenuItem key={sp.id} id={`speed:${sp.id}`} className={styles.item}>
                    {sp.label}
                  </MenuItem>
                ))}
              </MenuSection>
              <Separator className={styles.separator} />
              <MenuSection
                selectionMode="single"
                selectedKeys={[`density:${density}`]}
                disallowEmptySelection
                onSelectionChange={(s) => {
                  const next = single<DensityPreference>(s, 'density:');
                  if (next) setDensity(next);
                }}
              >
                <Header className={styles.header}>Density</Header>
                {densities.map((d) => (
                  <MenuItem key={d.id} id={`density:${d.id}`} className={styles.item}>
                    {d.label}
                  </MenuItem>
                ))}
              </MenuSection>
            </>
          )}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
